/**
 * Where a React render error goes once Sentry exists (#112).
 *
 * A seam rather than an import, for two reasons. `main.tsx` wires
 * `createRoot`'s `onCaughtError` / `onUncaughtError` at module scope, long
 * before the lazy Sentry chunk has loaded (or with no DSN, ever); and the
 * boundaries themselves must stay ignorant of any vendor. Until `initSentry`
 * installs a reporter, reporting is a no-op.
 *
 * Why the root and not the boundaries: `main.tsx` wraps the whole app in an
 * `ErrorBoundary`, and a boundary that catches an error means React never
 * rethrows it — so Sentry's global `onerror`/`unhandledrejection` capture sees
 * NOTHING of a render crash. `onCaughtError` is the one place those arrive.
 */
type Reporter = (error: unknown) => void

let reporter: Reporter | null = null

export function installErrorReporter(fn: Reporter): void {
  reporter = fn
}

/** Never throws: reporting a crash must not become a second crash. */
export function reportRenderError(error: unknown): void {
  try {
    reporter?.(error)
  } catch {
    // lost beats doubled
  }
}

export function __resetErrorReporter(): void {
  reporter = null
}
